import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, PenLine, ArrowRight, ArrowLeft } from "lucide-react";
import { Sign } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import SignDocumentForm from "@/components/SignDocumentForm";

const KEYS = ["consentimento_unificado"];

// Passo opcional após criar o paciente: assinar já no dispositivo ou deixar para depois.
export default function NewPatientDocsStep({ patient, onDone }) {
  const [tpls, setTpls] = useState([]);
  const [done, setDone] = useState({});
  const [signing, setSigning] = useState(null);
  useEffect(() => { Sign.templates().then((t) => setTpls(t.filter((x) => KEYS.includes(x.key)))).catch(() => {}); }, []);

  const open = async (key) => {
    try {
      const r = await Sign.create({ patient_id: patient.id, template_key: key, mode: "presencial" });
      setSigning({ id: r.id, key, doc: await Sign.document(r.id) });
    } catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível abrir o documento"); }
  };
  const back = () => { Sign.revoke(signing.id).catch(() => {}); setSigning(null); };
  const submit = async (data) => {
    try { await Sign.sign(signing.id, data); setDone((d) => ({ ...d, [signing.key]: true })); setSigning(null); toast.success("Documento assinado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao assinar"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onDone()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-100" data-testid="new-patient-docs-step">
        <DialogHeader>
          <DialogTitle className="text-slate-50 font-display">Documentos e consentimentos</DialogTitle>
          <DialogDescription className="text-slate-400">Opcional. Um único documento com uma assinatura final. Pode assinar agora neste dispositivo ou mais tarde na ficha de {patient.name}.</DialogDescription>
        </DialogHeader>
        {signing ? (
          <div className="space-y-3">
            <button onClick={back} data-testid="docs-step-back" className="text-xs text-slate-400 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Voltar à lista</button>
            <SignDocumentForm doc={signing.doc} onSubmit={submit} identityRequired />
          </div>
        ) : (
          <div className="space-y-3">
            {tpls.map((t) => (
              <div key={t.key} className="flex items-center gap-3 rounded-xl border border-slate-800 p-3 text-sm" data-testid={`docs-step-${t.key}`}>
                <div className="flex-1 min-w-0"><div className="text-slate-200">{t.title}</div><div className="text-xs text-slate-500">Versão {t.version}</div></div>
                {done[t.key] ? <span className="text-emerald-300 text-xs inline-flex items-center gap-1 shrink-0"><CheckCircle2 className="w-4 h-4" /> Assinado</span> : (
                  <button onClick={() => open(t.key)} data-testid={`docs-step-sign-${t.key}`} className="shrink-0 text-xs text-sky-300 inline-flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> Assinar agora</button>
                )}
              </div>
            ))}
            <button onClick={onDone} data-testid="docs-step-continue"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-medium px-4 py-2.5 text-sm transition-colors">
              {Object.keys(done).length ? "Continuar para a ficha clínica" : "Deixar para depois e continuar"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
